/**
 * The studio's to-do list.
 *
 * Not the board. The board is client deliverables — reels, posts, the things
 * a client is owed and sees on their page. This is everything else the studio
 * has to remember to do: call the printer, chase an invoice, reshoot a
 * product. It is the list the WhatsApp agent has always written to ClickUp,
 * and it stays in step with ClickUp both ways, so nobody has to change where
 * they look on the day this goes live.
 *
 * Grouped by when, not by status, because "what do I do next" is a question
 * about dates. Done is a filter rather than a column; a list that keeps
 * everything anybody ever finished on screen stops being a to-do list.
 */

import { CheckCircle2, Circle, ClipboardList, Plus, RefreshCw } from 'lucide-react'
import { useMemo, useState } from 'react'
import { api, ApiError } from '../api'
import { Avatar } from '../components/Avatar'
import { Button } from '../components/Button'
import { EmptyState } from '../components/EmptyState'
import { FilterBar, FilterSelect, SearchInput, SegmentedControl } from '../components/FilterBar'
import { PageHeader } from '../components/PageHeader'
import { PriorityBadge } from '../components/PriorityBadge'
import { useAppData } from '../context/AppDataContext'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import type { Priority } from '../data/types'
import { cn } from '../lib/cn'
import { daysUntil, formatDate } from '../lib/format'
import { useApi } from '../useApi'
import { TaskDrawer } from './tasks/TaskDrawer'
import { SyncStrip } from './tasks/SyncStrip'
import type { StudioTask } from './tasks/types'

const PRIORITY_LABEL: Record<string, Priority> = { low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent' }

type When = 'overdue' | 'today' | 'week' | 'later' | 'undated' | 'done'

const GROUPS: { key: When; label: string }[] = [
  { key: 'overdue', label: 'Overdue' },
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This week' },
  { key: 'later', label: 'Later' },
  { key: 'undated', label: 'No date' },
  { key: 'done', label: 'Done' }
]

function whenOf(t: StudioTask): When {
  if (t.status === 'done') return 'done'
  if (t.due === null) return 'undated'
  const d = daysUntil(t.due)
  if (d < 0) return 'overdue'
  if (d === 0) return 'today'
  if (d <= 7) return 'week'
  return 'later'
}

export default function Tasks() {
  const { clients, currentClients: activeClients, directory } = useAppData()
  const { currentUser, hasRole } = useAuth()
  const { showToast } = useToast()
  const me = currentUser?.linkedPersonaId ?? null

  const [whose, setWhose] = useState<'mine' | 'everyone'>(me === null ? 'everyone' : 'mine')
  const [client, setClient] = useState('all')
  const [search, setSearch] = useState('')
  const [showDone, setShowDone] = useState(false)
  const [open, setOpen] = useState<StudioTask | 'new' | null>(null)
  // Ticked here before the server answers, so the tick is instant on a phone.
  const [ticking, setTicking] = useState<Record<string, StudioTask['status']>>({})

  const { result, reload } = useApi<StudioTask[]>('/tasks', {
    query: {
      ...(whose === 'mine' && me !== null ? { assignee: 'me' } : {}),
      ...(client === 'all' ? {} : { client }),
      ...(showDone ? { done: 'true' } : {})
    }
  })
  const tasks = result.data ?? []

  // A tick holds until the list it was made against is replaced by a fresh
  // one, so the box does not flick back between the save and the reload.
  const [seen, setSeen] = useState(result.data)
  if (result.data !== seen) {
    setSeen(result.data)
    setTicking({})
  }

  const clientById = useMemo(() => new Map(clients.map((c) => [c.id, c])), [clients])
  const personById = useMemo(() => new Map(directory.map((p) => [p.id, p])), [directory])

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase()
    const out = new Map<When, StudioTask[]>()
    for (const t0 of tasks) {
      const t = ticking[t0.id] === undefined ? t0 : { ...t0, status: ticking[t0.id]! }
      if (q !== '' && !t.title.toLowerCase().includes(q) && !t.tags.some((g) => g.includes(q))) continue
      const w = whenOf(t)
      if (w === 'done' && !showDone) continue
      out.set(w, [...(out.get(w) ?? []), t])
    }
    for (const list of out.values()) {
      list.sort((a, b) => (a.due ?? '9999').localeCompare(b.due ?? '9999') || a.title.localeCompare(b.title))
    }
    return out
  }, [tasks, ticking, search, showDone])

  const openCount = tasks.filter((t) => t.status !== 'done').length

  async function tick(t: StudioTask, next: StudioTask['status'] = t.status === 'done' ? 'todo' : 'done') {
    const was = t.status
    setTicking((s) => ({ ...s, [t.id]: next }))
    try {
      await api(`/tasks/${t.id}/status`, { method: 'PATCH', body: { status: next } })
      reload()
      // A tick is one tap from the wrong row, on a phone more than anywhere.
      // Undo puts it back where it was — in progress stays in progress.
      if (next === 'done') {
        showToast(`"${t.title}" is done.`, 'success', { undo: () => tick({ ...t, status: 'done' }, was) })
      }
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : 'That did not save.', 'error')
      setTicking((s) => {
        const { [t.id]: _gone, ...rest } = s
        return rest
      })
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        variant="dark"
        title="Tasks"
        description="Everything the studio has to get done that is not a piece for a client."
        actions={
          hasRole('PARTNER', 'MANAGER', 'TEAM') ? (
            <Button onClick={() => setOpen('new')}>
              <Plus size={16} /> Add a task
            </Button>
          ) : undefined
        }
      />

      {hasRole('PARTNER') && <SyncStrip />}

      <FilterBar>
        {me !== null && (
          <SegmentedControl
            value={whose}
            onChange={(v) => setWhose(v as 'mine' | 'everyone')}
            options={[
              { label: 'Mine', value: 'mine' },
              { label: 'Everyone', value: 'everyone' }
            ]}
          />
        )}
        <SearchInput value={search} onChange={setSearch} placeholder="Search tasks…" className="w-full sm:w-56" />
        <FilterSelect
          label="Filter by client"
          value={client}
          onChange={setClient}
          options={[
            { label: 'Any client', value: 'all' },
            ...activeClients.map((c) => ({ label: c.name, value: c.id }))
          ]}
        />
        <button
          onClick={() => setShowDone((v) => !v)}
          aria-pressed={showDone}
          className={cn(
            'flex h-9 items-center gap-1.5 rounded-lg border px-3 text-sm transition-colors',
            showDone ? 'border-shell-text-active bg-shell-text-active text-shell' : 'border-shell-border bg-shell text-shell-text hover:bg-shell-border/40'
          )}
        >
          <CheckCircle2 size={13} /> Show done
        </button>
      </FilterBar>

      {result.state === 'failed' && (
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
          <span>{result.error.message}</span>
          <button onClick={reload} className="flex items-center gap-1 font-medium">
            <RefreshCw size={13} /> Try again
          </button>
        </div>
      )}

      {result.state === 'loaded' && groups.size === 0 ? (
        <EmptyState
          icon={ClipboardList}
          title={whose === 'mine' ? 'Nothing on your list' : 'Nothing on the list'}
          description={
            search !== '' || client !== 'all'
              ? 'Nothing matches. Try clearing the search or the client.'
              : 'Tasks added here, in ClickUp or through the WhatsApp agent all land on this list.'
          }
        />
      ) : (
        <div className="flex flex-col gap-6">
          {GROUPS.filter((g) => groups.has(g.key)).map((g) => (
            <section key={g.key} aria-labelledby={`tasks-${g.key}`}>
              <h2
                id={`tasks-${g.key}`}
                className={cn(
                  'mb-2 flex items-baseline gap-2 px-1 text-xs font-semibold uppercase tracking-wider',
                  g.key === 'overdue' ? 'text-danger-600' : 'text-ink-500'
                )}
              >
                {g.label}
                <span className="font-medium tabular-nums text-ink-400">{groups.get(g.key)!.length}</span>
              </h2>
              <ul className="divide-y divide-shell-border overflow-hidden rounded-2xl border border-shell-border bg-shell shadow-card">
                {groups.get(g.key)!.map((t) => {
                  const c = t.client_id === null ? undefined : clientById.get(t.client_id)
                  const done = t.status === 'done'
                  const people = t.assignee_ids.map((id) => personById.get(id)).filter((p) => p !== undefined)
                  return (
                    <li key={t.id} className="flex items-start gap-3 px-3.5 py-3 sm:px-4">
                      <button
                        aria-label={done ? `Mark "${t.title}" as not done` : `Mark "${t.title}" as done`}
                        onClick={() => void tick(t)}
                        className="-m-1.5 mt-[-3px] shrink-0 rounded-full p-1.5 text-ink-300 transition-colors hover:text-success-500"
                      >
                        {done ? <CheckCircle2 size={20} className="text-success-500" /> : <Circle size={20} />}
                      </button>
                      <button onClick={() => setOpen(t)} className="flex min-w-0 flex-1 flex-col gap-1.5 text-left">
                        <span
                          className={cn(
                            'text-[15px] leading-snug sm:text-sm',
                            done ? 'text-ink-400 line-through' : 'font-medium text-shell-text-active'
                          )}
                        >
                          {t.title}
                        </span>
                        <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-ink-500">
                          {c !== undefined && (
                            <span className="flex items-center gap-1.5">
                              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: c.color }} />
                              {c.name}
                            </span>
                          )}
                          {t.due !== null && (
                            <span className={cn('tabular-nums', g.key === 'overdue' && 'font-medium text-danger-600')}>
                              {formatDate(t.due)}
                            </span>
                          )}
                          {t.priority !== null && (t.priority === 'high' || t.priority === 'urgent') && (
                            <PriorityBadge priority={PRIORITY_LABEL[t.priority]!} className="py-0.5" />
                          )}
                          {t.status === 'in_progress' && (
                            <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-700">In progress</span>
                          )}
                          {t.source !== 'hapi' && <SourceChip source={t.source} />}
                          {t.tags.slice(0, 3).map((tag) => (
                            <span key={tag} className="rounded-full bg-shell-border/40 px-2 py-0.5 text-[11px] text-shell-text">
                              {tag}
                            </span>
                          ))}
                        </span>
                      </button>
                      {people.length > 0 && (
                        <div className="flex shrink-0 -space-x-1.5 pt-0.5">
                          {people.slice(0, 3).map((p) => (
                            <Avatar key={p.id} name={p.name} color={p.color} size="xs" ring />
                          ))}
                        </div>
                      )}
                    </li>
                  )
                })}
              </ul>
            </section>
          ))}
        </div>
      )}

      {result.state === 'loaded' && openCount > 0 && (
        <p className="px-1 text-xs tabular-nums text-ink-400">
          {openCount} open{whose === 'mine' ? ' with you' : ''}
        </p>
      )}

      <TaskDrawer
        task={open}
        onClose={() => setOpen(null)}
        onSaved={() => {
          setOpen(null)
          reload()
        }}
      />
    </div>
  )
}

/** Where a task was made, when it was not made here. */
export function SourceChip({ source }: { source: StudioTask['source'] }) {
  return source === 'agent' ? (
    <span className="rounded-full bg-success-50 px-2 py-0.5 text-[11px] font-medium text-success-700">via WhatsApp</span>
  ) : source === 'clickup' ? (
    <span className="rounded-full bg-info-50 px-2 py-0.5 text-[11px] font-medium text-info-700">from ClickUp</span>
  ) : null
}
